import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  BookOpen, Calendar, FileText, Paperclip, Download, Video,
  Image as ImageIcon, File, Sparkles, Loader2, Maximize2, X,
  Camera, Pencil, ClipboardList, ArrowRight,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { getSignedFileUrl, getSignedFileUrls } from "@/lib/storage-utils";
import GenerateExplanationButton from "@/components/lesson/GenerateExplanationButton";
import InteractiveWhiteboard from "@/components/lesson/InteractiveWhiteboard";
import VideoRecorder from "@/components/VideoRecorder";
import AddAssignmentDialog from "@/components/dialogs/AddAssignmentDialog";
import GenerateQuestionsDialog from "@/components/dialogs/GenerateQuestionsDialog";
import LessonActivitiesManager from "@/components/activities/LessonActivitiesManager";

const LessonDetail = () => {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { profile, roles } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const nurseryId = profile?.nursery_id;

  const [showRecorder, setShowRecorder] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [fullscreenPdf, setFullscreenPdf] = useState<{ url: string; name: string; type?: string } | null>(null);
  const [assignmentFromLesson, setAssignmentFromLesson] = useState<{ lessonId: string; classId: string | null; title: string } | null>(null);
  const [generateLesson, setGenerateLesson] = useState<{ id: string; title: string; content: string; classId: string | null } | null>(null);

  // Close fullscreen on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") setFullscreenPdf(null); };
    if (fullscreenPdf) window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [fullscreenPdf]);

  // Fetch lesson
  const { data: lesson, isLoading } = useQuery({
    queryKey: ["lesson-detail", lessonId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lessons")
        .select("*, classes(name)")
        .eq("id", lessonId!)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!lessonId,
  });

  // Fetch classes for assignment dialog
  const { data: classes } = useQuery({
    queryKey: ["classes-list", nurseryId],
    queryFn: async () => {
      const { data } = await supabase.from("classes").select("id, name").eq("nursery_id", nurseryId!);
      return data || [];
    },
    enabled: !!nurseryId,
  });

  // Fetch lesson files
  const { data: lessonFiles } = useQuery({
    queryKey: ["lesson-files", lessonId],
    queryFn: async () => {
      const { data } = await supabase
        .from("lesson_files")
        .select("*")
        .eq("lesson_id", lessonId!)
        .order("created_at", { ascending: true });
      if (!data || data.length === 0) return [];
      const signedUrls = await getSignedFileUrls(data);
      return data.map((f: any) => ({ ...f, signed_url: signedUrls[f.id] || f.file_url }));
    },
    enabled: !!lessonId,
  });

  // Resolve cover image
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  useEffect(() => {
    if (lesson?.media_url) {
      getSignedFileUrl(lesson.media_url).then(setCoverUrl);
    } else {
      setCoverUrl(null);
    }
  }, [lesson?.media_url]);

  const formatDate = (d: string | null) => {
    if (!d) return "-";
    try { return format(new Date(d), "d MMM yyyy", { locale: ar }); } catch { return d; }
  };

  const handleVideoReady = useCallback(async (blob: Blob, filename: string) => {
    if (!lesson || !nurseryId) return;
    setUploadingVideo(true);
    try {
      const path = `${nurseryId}/${lesson.id}/${crypto.randomUUID()}.webm`;
      const { error: uploadError } = await supabase.storage
        .from("lesson-files")
        .upload(path, blob, { contentType: "video/webm" });
      if (uploadError) throw uploadError;

      const { error: dbError } = await supabase.from("lesson_files").insert({
        lesson_id: lesson.id,
        nursery_id: nurseryId,
        file_name: filename,
        file_url: path,
        file_type: "video",
        file_size: blob.size,
      });
      if (dbError) throw dbError;

      toast({ title: "تم رفع الفيديو بنجاح" });
      setShowRecorder(false);
      queryClient.invalidateQueries({ queryKey: ["lesson-files", lesson.id] });
    } catch (err: any) {
      toast({ title: "خطأ في رفع الفيديو", description: err.message, variant: "destructive" });
    } finally {
      setUploadingVideo(false);
    }
  }, [lesson, nurseryId, toast, queryClient]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="text-center py-20 space-y-4">
        <p className="text-muted-foreground">لم يتم العثور على الدرس</p>
        <Button variant="outline" onClick={() => navigate("/dashboard/lessons")}>العودة للدروس</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/dashboard/lessons")} className="gap-2">
          <ArrowRight className="w-4 h-4" />
          العودة للدروس
        </Button>
      </div>

      {/* Hero Cover */}
      {coverUrl ? (
        <div className="relative rounded-2xl overflow-hidden shadow-elevated">
          <img src={coverUrl} alt={lesson.title} className="w-full h-64 object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
          <div className="absolute bottom-0 right-0 left-0 p-6">
            <h1 className="text-2xl font-bold text-white mb-2">{lesson.title}</h1>
            <div className="flex items-center gap-3">
              <Badge className="bg-white/20 text-white border-0">
                <FileText className="w-3 h-3 ml-1" />
                {(lesson as any).classes?.name || "بدون فصل"}
              </Badge>
              {lesson.scheduled_at && (
                <span className="flex items-center gap-1 text-white/80 text-sm">
                  <Calendar className="w-3.5 h-3.5" />
                  {formatDate(lesson.scheduled_at)}
                </span>
              )}
            </div>
          </div>
        </div>
      ) : (
        <Card className="shadow-elevated overflow-hidden">
          <div className="bg-gradient-to-br from-primary/10 to-secondary/10 p-8">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-primary/20 flex items-center justify-center">
                <BookOpen className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">{lesson.title}</h1>
                <div className="flex items-center gap-3 mt-2">
                  <Badge variant="outline">
                    <FileText className="w-3 h-3 ml-1" />
                    {(lesson as any).classes?.name || "بدون فصل"}
                  </Badge>
                  {lesson.scheduled_at && (
                    <span className="flex items-center gap-1 text-muted-foreground text-sm">
                      <Calendar className="w-3.5 h-3.5" />
                      {formatDate(lesson.scheduled_at)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Content + Actions in grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - 2 cols */}
        <div className="lg:col-span-2 space-y-6">
          {/* Lesson Content */}
          {lesson.content && (
            <Card className="shadow-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <BookOpen className="w-4 h-4 text-primary" />
                  محتوى الدرس
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-wrap">{lesson.content}</p>
              </CardContent>
            </Card>
          )}

          {/* Files */}
          {lessonFiles && lessonFiles.length > 0 && (
            <Card className="shadow-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Paperclip className="w-4 h-4 text-primary" />
                  المرفقات ({lessonFiles.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {lessonFiles.map((f: any) => {
                  const isVideo = f.file_type === "video";
                  const isImage = f.file_type === "image";
                  const isWord = f.file_type === "word" || f.file_name?.endsWith(".docx") || f.file_name?.endsWith(".doc");
                  const isPpt = f.file_name?.endsWith(".pptx") || f.file_name?.endsWith(".ppt");
                  const isOffice = isWord || isPpt;
                  const officeViewerUrl = isOffice ? `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(f.signed_url)}` : null;
                  const icon = f.file_type === "pdf" || isWord ? <FileText className="w-4 h-4" />
                    : isPpt ? <FileText className="w-4 h-4" />
                    : isVideo ? <Video className="w-4 h-4" />
                    : isImage ? <ImageIcon className="w-4 h-4" />
                    : <File className="w-4 h-4" />;
                  return (
                    <div key={f.id}>
                      {isImage && <img src={f.signed_url} alt={f.file_name} className="rounded-lg max-h-64 object-cover mb-2" />}
                      {isVideo && <video src={f.signed_url} controls className="rounded-lg w-full mb-2" />}
                      {f.file_type === "pdf" && (
                        <div className="relative mb-2">
                          <iframe src={f.signed_url} className="w-full h-[500px] rounded-lg border" title={f.file_name} />
                          <Button variant="secondary" size="icon" className="absolute top-2 left-2 h-8 w-8 shadow-md"
                            onClick={() => setFullscreenPdf({ url: f.signed_url, name: f.file_name, type: "pdf" })}>
                            <Maximize2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      {isOffice && officeViewerUrl && (
                        <div className="relative mb-2">
                          <iframe src={officeViewerUrl} className="w-full h-[500px] rounded-lg border" title={f.file_name} />
                          <Button variant="secondary" size="icon" className="absolute top-2 left-2 h-8 w-8 shadow-md"
                            onClick={() => setFullscreenPdf({ url: officeViewerUrl, name: f.file_name, type: "office" })}>
                            <Maximize2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      <a href={f.signed_url} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 bg-muted/50 rounded-md p-3 text-sm hover:bg-muted transition-colors">
                        {icon}
                        <span className="flex-1 truncate">{f.file_name}</span>
                        <Badge variant="secondary" className="text-xs">
                          {f.file_type === "pdf" ? "PDF" : isWord ? "Word" : isPpt ? "PowerPoint" : f.file_type === "video" ? "فيديو" : f.file_type === "image" ? "صورة" : "ملف"}
                        </Badge>
                        <Download className="w-4 h-4 text-muted-foreground" />
                      </a>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Interactive Activities Manager */}
          {nurseryId && (
            <LessonActivitiesManager lessonId={lesson.id} nurseryId={nurseryId} />
          )}

          {/* Interactive Whiteboard */}
          <Card className="shadow-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Pencil className="w-4 h-4 text-primary" />
                📝 السبورة التفاعلية
              </CardTitle>
              <p className="text-xs text-muted-foreground">ارسم واكتب على السبورة لشرح الدرس بطريقة تفاعلية</p>
            </CardHeader>
            <CardContent>
              <InteractiveWhiteboard height={450} />
            </CardContent>
          </Card>

          {/* Video Recorder */}
          {showRecorder && (
            <Card className="shadow-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Camera className="w-4 h-4 text-primary" />
                  تسجيل فيديو شرح الدرس
                </CardTitle>
              </CardHeader>
              <CardContent>
                {uploadingVideo ? (
                  <div className="flex flex-col items-center gap-2 py-8">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">جاري رفع الفيديو...</p>
                  </div>
                ) : (
                  <VideoRecorder onVideoReady={handleVideoReady} onCancel={() => setShowRecorder(false)} maxDurationSeconds={600} />
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar Actions - 1 col (hidden for parents) */}
        {!roles.includes("parent") && (
        <div className="space-y-4">
          {/* AI Explanation */}
          {nurseryId && (
            <Card className="shadow-card border-primary/20">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">📚</span>
                  <div>
                    <p className="text-sm font-semibold text-foreground">الكراسة الذكية</p>
                    <p className="text-xs text-muted-foreground">شرائح ملونة وممتعة بالذكاء الاصطناعي</p>
                  </div>
                </div>
                <GenerateExplanationButton
                  lessonId={lesson.id}
                  lessonTitle={lesson.title}
                  lessonContent={lesson.content}
                  nurseryId={nurseryId}
                />
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <Card className="shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">إجراءات سريعة</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {!showRecorder && (
                <Button variant="outline" className="w-full justify-start gap-2" onClick={() => setShowRecorder(true)}>
                  <Camera className="w-4 h-4" />
                  تسجيل فيديو
                </Button>
              )}
              <Button variant="outline" className="w-full justify-start gap-2"
                onClick={() => setAssignmentFromLesson({
                  lessonId: lesson.id,
                  classId: lesson.class_id,
                  title: `واجب: ${lesson.title}`,
                })}>
                <ClipboardList className="w-4 h-4" />
                إنشاء واجب
              </Button>
              <Button variant="outline" className="w-full justify-start gap-2"
                onClick={() => setGenerateLesson({
                  id: lesson.id,
                  title: lesson.title,
                  content: lesson.content || "",
                  classId: lesson.class_id,
                })}>
                <Sparkles className="w-4 h-4" />
                🤖 إنشاء اختبار بالذكاء
              </Button>
            </CardContent>
          </Card>
        </div>
        )}
      </div>

      {/* Fullscreen PDF Overlay */}
      {fullscreenPdf && (
        <div className="fixed inset-0 z-50 bg-black/80 flex flex-col" onClick={() => setFullscreenPdf(null)}>
          <div className="flex items-center justify-between p-3 bg-background/90 backdrop-blur-sm border-b" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <FileText className="w-4 h-4" />
              {fullscreenPdf.name}
            </div>
            <div className="flex items-center gap-2">
              <a href={fullscreenPdf.url} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm"><Download className="w-3.5 h-3.5 ml-1" />تحميل</Button>
              </a>
              <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setFullscreenPdf(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>
          <iframe src={fullscreenPdf.url} className="flex-1 w-full bg-white" title={fullscreenPdf.name} onClick={(e) => e.stopPropagation()} />
        </div>
      )}

      {/* Generate Questions Dialog */}
      {nurseryId && generateLesson && (
        <GenerateQuestionsDialog
          open={!!generateLesson}
          onOpenChange={(o) => !o && setGenerateLesson(null)}
          lessonId={generateLesson.id}
          lessonTitle={generateLesson.title}
          lessonContent={generateLesson.content}
          nurseryId={nurseryId}
          classId={generateLesson.classId}
          onSuccess={() => {}}
        />
      )}

      {/* Create Assignment Dialog */}
      {nurseryId && assignmentFromLesson && (
        <AddAssignmentDialog
          classes={classes || []}
          nurseryId={nurseryId}
          onSuccess={() => setAssignmentFromLesson(null)}
          controlledOpen={!!assignmentFromLesson}
          onOpenChange={(o) => !o && setAssignmentFromLesson(null)}
          defaultLessonId={assignmentFromLesson.lessonId}
          defaultClassId={assignmentFromLesson.classId || undefined}
          defaultTitle={assignmentFromLesson.title}
        />
      )}
    </div>
  );
};

export default LessonDetail;
